import React, { useState, useRef } from 'react';
import { apiUrl } from '../api';

// Sample templates data for easy download
const SAMPLE_MEDICINE_DATA = [
  { 'Medicine Name': 'PARACETAMOL 650MG', 'Company': 'DOLO PHARMA', 'Batch': 'DOL2025', 'Expiry': '12/27', 'Quantity': 100, 'Purchase Rate': 22.50, 'MRP': 31.00, 'Shelf/Rack': 'A-1' },
  { 'Medicine Name': 'AZITHROMYCIN 500MG', 'Company': 'CIPLA', 'Batch': 'AZ5001', 'Expiry': '08/26', 'Quantity': 50, 'Purchase Rate': 75.00, 'MRP': 115.00, 'Shelf/Rack': 'B-2' },
  { 'Medicine Name': 'PANTOPRAZOLE 40MG', 'Company': 'ALKEM', 'Batch': 'PAN88', 'Expiry': '05/27', 'Quantity': 80, 'Purchase Rate': 45.00, 'MRP': 68.00, 'Shelf/Rack': 'A-3' },
  { 'Medicine Name': 'CETIRIZINE 10MG', 'Company': 'MANKIND', 'Batch': 'CET09', 'Expiry': '11/26', 'Quantity': 120, 'Purchase Rate': 12.00, 'MRP': 18.50, 'Shelf/Rack': 'C-1' },
  { 'Medicine Name': 'AMOXICILLIN 500MG', 'Company': 'SUN PHARMA', 'Batch': 'AMX33', 'Expiry': '04/27', 'Quantity': 60, 'Purchase Rate': 55.00, 'MRP': 85.00, 'Shelf/Rack': 'B-1' }
];

const SAMPLE_CUSTOMER_DATA = [
  { 'Customer Name': 'RAMESH PATIL', 'Mobile': '9822012345', 'Due Amount': 850.00, 'Address': 'Station Road, Shop 4' },
  { 'Customer Name': 'GANESH JADHAV', 'Mobile': '9890123456', 'Due Amount': 1200.00, 'Address': 'Main Market' },
  { 'Customer Name': 'SURESH DESHMUKH', 'Mobile': '9765432100', 'Due Amount': 450.00, 'Address': 'Shivaji Nagar' }
];

const SAMPLE_STOCKIST_DATA = [
  { 'Supplier Name': 'MAHAVIR PHARMA DISTRIBUTORS', 'Phone': '9822112233', 'GSTIN': '27ABCDE1234F1Z5', 'Address': 'Navi Peth, Pune' },
  { 'Supplier Name': 'NEW MEDICO AGENCIES', 'Phone': '9850556677', 'GSTIN': '27XYZAB9876C1Z2', 'Address': 'Market Yard' }
];

const DataMigrationModal = ({ isOpen, onClose, onImportSuccess }) => {
  const [importCategory, setImportCategory] = useState('medicines'); // 'medicines' | 'credit' | 'stockists'
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [previewColumns, setPreviewColumns] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  // Helper to dynamically load SheetJS if not already present on window
  const ensureSheetJS = () => {
    return new Promise((resolve, reject) => {
      if (window.XLSX) return resolve(window.XLSX);
      const existing = document.querySelector('script[src*="xlsx"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(window.XLSX));
        existing.addEventListener('error', () => reject(new Error('xlsx load failed')));
        return;
      }
      const script = document.createElement('script');
      const vendorUrl = `${process.env.PUBLIC_URL || ''}/vendor/xlsx.full.min.js`;
      script.src = vendorUrl;
      script.onload = () => resolve(window.XLSX);
      script.onerror = () => {
        // Fallback to CDN if local bundle fails
        const fallback = document.createElement('script');
        fallback.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
        fallback.onload = () => resolve(window.XLSX);
        fallback.onerror = () => reject(new Error('Failed to load Excel parser library'));
        document.head.appendChild(fallback);
      };
      document.head.appendChild(script);
    });
  };

  // Download ready-to-fill sample template
  const handleDownloadTemplate = async (format = 'xlsx') => {
    try {
      const XLSX = await ensureSheetJS();
      let data = [];
      let filename = 'Sample_Template';

      if (importCategory === 'medicines') {
        data = SAMPLE_MEDICINE_DATA;
        filename = 'MediFlow_Sample_Medicines_Stock';
      } else if (importCategory === 'credit') {
        data = SAMPLE_CUSTOMER_DATA;
        filename = 'MediFlow_Sample_Customer_Udhari';
      } else {
        data = SAMPLE_STOCKIST_DATA;
        filename = 'MediFlow_Sample_Suppliers_Stockists';
      }

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sample_Data');

      if (format === 'csv') {
        XLSX.writeFile(wb, `${filename}.csv`, { bookType: 'csv' });
      } else {
        XLSX.writeFile(wb, `${filename}.xlsx`, { bookType: 'xlsx' });
      }
    } catch (err) {
      alert('Error downloading template: ' + err.message);
    }
  };

  // Smart field matcher for diverse column names from old software
  const findValue = (row, fieldVariants) => {
    for (const key of Object.keys(row)) {
      const normalizedKey = key.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
      for (const variant of fieldVariants) {
        const normalizedVariant = variant.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (normalizedKey === normalizedVariant || normalizedKey.includes(normalizedVariant)) {
          return row[key];
        }
      }
    }
    return '';
  };

  // Handle file upload and parse sheet rows
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setStatusMsg(null);
    setIsProcessing(true);

    try {
      const XLSX = await ensureSheetJS();
      const reader = new FileReader();

      reader.onload = (evt) => {
        try {
          const binaryStr = evt.target.result;
          const workbook = XLSX.read(binaryStr, { type: 'binary' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonRows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

          if (!jsonRows || jsonRows.length === 0) {
            throw new Error('The uploaded file appears to be empty or has no data rows.');
          }

          setParsedRows(jsonRows);
          setPreviewColumns(Object.keys(jsonRows[0]));
          setStatusMsg({
            type: 'info',
            text: `Detected ${jsonRows.length} rows from sheet "${firstSheetName}". Review preview below and click Import.`
          });
        } catch (parseErr) {
          console.error(parseErr);
          setStatusMsg({ type: 'error', text: 'Error reading file content: ' + parseErr.message });
          setParsedRows([]);
        } finally {
          setIsProcessing(false);
        }
      };

      reader.readAsBinaryString(selectedFile);
    } catch (err) {
      setIsProcessing(false);
      setStatusMsg({ type: 'error', text: 'Parser library loading failed: ' + err.message });
    }
  };

  // Execute import into MediFlow database
  const handleExecuteImport = async () => {
    if (parsedRows.length === 0) {
      alert('No data rows to import. Please select a valid Excel or CSV file first.');
      return;
    }

    try {
      setIsProcessing(true);
      const today = new Date().toISOString().split('T')[0];

      if (importCategory === 'medicines') {
        // Map to standard MediFlow items
        const items = parsedRows.map((r, i) => {
          const name = findValue(r, ['medicine', 'name', 'item', 'product', 'itemname', 'productname', 'description']) || `Medicine-${i+1}`;
          const batch = findValue(r, ['batch', 'batchno', 'bno', 'lot']) || 'BATCH-01';
          const exp = findValue(r, ['exp', 'expiry', 'expdate', 'expirydate']) || '12/28';
          const qty = parseInt(findValue(r, ['qty', 'quantity', 'stock', 'closingstock', 'bal', 'balance', 'units'])) || 1;
          const rate = parseFloat(findValue(r, ['rate', 'purchaserate', 'cost', 'costprice', 'prate', 'buyprice'])) || 0;
          const mrp = parseFloat(findValue(r, ['mrp', 'maxretailprice', 'salerate', 'price'])) || (rate > 0 ? rate * 1.2 : 0);
          const company = findValue(r, ['company', 'mfg', 'manufacturer']) || '';
          const shelf = findValue(r, ['shelf', 'rack', 'location']) || '';

          return {
            productName: String(name).trim().toUpperCase(),
            batch: String(batch).trim().toUpperCase(),
            exp: String(exp).trim(),
            qty: qty > 0 ? qty : 1,
            rate: rate >= 0 ? rate : 0,
            costPrice: rate >= 0 ? rate : 0,
            mrp: mrp >= 0 ? mrp : rate,
            company: String(company).trim(),
            shelf: String(shelf).trim(),
            amount: (qty > 0 ? qty : 1) * (rate >= 0 ? rate : 0)
          };
        }).filter(it => it.productName && it.productName.trim() !== '');

        if (items.length === 0) {
          throw new Error('Could not identify medicine names in the sheet. Please ensure a column named "Medicine Name" or "Item" is present.');
        }

        // Post as an opening stock inward purchase record
        const purchasePayload = {
          supplierName: 'Old App Opening Stock (स्थलांतरित स्टॉक)',
          supplierGst: '',
          invoiceNo: `IMPORT-${Date.now().toString().slice(-6)}`,
          date: today,
          paymentType: 'Stock Migration',
          grossAmount: items.reduce((s, it) => s + it.amount, 0),
          netAmount: items.reduce((s, it) => s + it.amount, 0),
          items: items
        };

        const res = await fetch(apiUrl('/api/purchases'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(purchasePayload)
        });

        if (!res.ok) throw new Error('Failed to save imported stock into backend database');

        // Also save to customMedicines in localStorage for instant billing suggestion
        try {
          const custom = JSON.parse(localStorage.getItem('customMedicines') || '[]');
          items.forEach(it => {
            if (!custom.some(c => c.name.toLowerCase() === it.productName.toLowerCase())) {
              custom.push({ name: it.productName, company: it.company, mrp: it.mrp, rate: it.rate });
            }
          });
          localStorage.setItem('customMedicines', JSON.stringify(custom));
        } catch (e) {
          // ignore localStorage
        }

        setStatusMsg({
          type: 'success',
          text: `🎉 Successfully imported ${items.length} medicines into your MediFlow stock and catalog!`
        });

      } else if (importCategory === 'credit') {
        // Map customer credit ledgers
        const creditAccounts = parsedRows.map((r, i) => {
          const name = findValue(r, ['customer', 'name', 'client', 'party', 'custname', 'account']) || `Customer-${i+1}`;
          const phone = findValue(r, ['mobile', 'phone', 'contact', 'mob', 'cell']) || '';
          const due = parseFloat(findValue(r, ['due', 'dueamount', 'balance', 'udhari', 'closingbalance', 'amount', 'outstanding'])) || 0;
          const address = findValue(r, ['address', 'addr', 'city']) || '';

          return {
            name: String(name).trim().toUpperCase(),
            phone: String(phone).trim(),
            dueAmount: due,
            address: String(address).trim(),
            lastUpdate: today
          };
        }).filter(c => c.name && c.name.trim() !== '');

        if (creditAccounts.length === 0) {
          throw new Error('Could not identify customer names in the sheet.');
        }

        // Restore into Firestore / database via bulk restore
        const res = await fetch(apiUrl('/api/backups/restore-upload'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credit: creditAccounts })
        });

        if (!res.ok) throw new Error('Failed to save credit ledgers into database');

        setStatusMsg({
          type: 'success',
          text: `🎉 Successfully imported ${creditAccounts.length} customer credit / udhari accounts into MediFlow!`
        });

      } else if (importCategory === 'stockists') {
        // Map stockists / suppliers
        const suppliers = parsedRows.map((r, i) => {
          const name = findValue(r, ['supplier', 'stockist', 'dealer', 'distributor', 'party', 'name']) || `Supplier-${i+1}`;
          const phone = findValue(r, ['phone', 'mobile', 'contact', 'mob']) || '';
          const gst = findValue(r, ['gst', 'gstin', 'gstno']) || '';
          const address = findValue(r, ['address', 'city', 'location']) || '';

          return {
            name: String(name).trim().toUpperCase(),
            phone: String(phone).trim(),
            gst: String(gst).trim().toUpperCase(),
            address: String(address).trim(),
            totalPurchases: 0,
            totalBills: 0
          };
        }).filter(s => s.name && s.name.trim() !== '');

        const res = await fetch(apiUrl('/api/backups/restore-upload'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ stockist: suppliers })
        });

        if (!res.ok) throw new Error('Failed to save suppliers into database');

        setStatusMsg({
          type: 'success',
          text: `🎉 Successfully imported ${suppliers.length} distributors and stockists into MediFlow!`
        });
      }

      // Notify parent to refresh data
      if (onImportSuccess) {
        setTimeout(() => {
          onImportSuccess();
        }, 1200);
      }

    } catch (err) {
      console.error(err);
      setStatusMsg({ type: 'error', text: 'Import failed: ' + err.message });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md z-[350] flex items-center justify-center p-3 sm:p-5 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-4xl max-h-[92vh] rounded-[2rem] shadow-2xl flex flex-col overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white p-5 sm:p-6 border-b border-slate-800 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-500 to-teal-400 flex items-center justify-center text-slate-950 font-bold text-lg shadow-md shrink-0">
              📥
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg sm:text-xl font-black text-white">Import Old Software Data (डेटा स्थलांतर)</h2>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">
                  Excel & CSV
                </span>
              </div>
              <p className="text-xs text-slate-300 font-medium mt-0.5">
                Migrate medicines, stock, customer udhari ledgers, and suppliers from your old pharmacy software into MediFlow.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center font-bold text-lg transition-colors ml-2"
          >
            ×
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5">
          
          {/* Status Message */}
          {statusMsg && (
            <div className={`p-4 rounded-xl flex items-center justify-between text-xs font-bold animate-in fade-in ${
              statusMsg.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' :
              statusMsg.type === 'error' ? 'bg-rose-50 text-rose-800 border border-rose-200' :
              'bg-blue-50 text-blue-800 border border-blue-200'
            }`}>
              <div className="flex items-center gap-2">
                <span>{statusMsg.type === 'success' ? '✓' : statusMsg.type === 'error' ? '!' : 'ℹ️'}</span>
                <span>{statusMsg.text}</span>
              </div>
              <button onClick={() => setStatusMsg(null)} className="text-slate-400 hover:text-slate-700 font-bold ml-2">×</button>
            </div>
          )}

          {/* Step 1: Select What to Import */}
          <div>
            <label className="block text-xs font-black text-slate-800 uppercase tracking-wider mb-2">
              Step 1: Select What You Want to Import (काय इम्पोर्ट करायचे आहे निवडा):
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => {
                  setImportCategory('medicines');
                  setFile(null);
                  setParsedRows([]);
                  setStatusMsg(null);
                }}
                className={`p-3.5 rounded-2xl border text-left transition-all ${
                  importCategory === 'medicines'
                    ? 'border-blue-600 bg-blue-50/60 ring-2 ring-blue-500/20 text-slate-900 shadow-sm'
                    : 'border-slate-200 hover:bg-slate-50 text-slate-600'
                }`}
              >
                <div className="text-base font-black flex items-center gap-1.5 text-blue-700">
                  <span>💊</span>
                  <span>Medicines & Stock</span>
                </div>
                <p className="text-[11px] text-slate-500 font-semibold mt-1">
                  Medicine names, batch, expiry, quantity, purchase rate, and MRP.
                </p>
              </button>

              <button
                type="button"
                onClick={() => {
                  setImportCategory('credit');
                  setFile(null);
                  setParsedRows([]);
                  setStatusMsg(null);
                }}
                className={`p-3.5 rounded-2xl border text-left transition-all ${
                  importCategory === 'credit'
                    ? 'border-rose-600 bg-rose-50/60 ring-2 ring-rose-500/20 text-slate-900 shadow-sm'
                    : 'border-slate-200 hover:bg-slate-50 text-slate-600'
                }`}
              >
                <div className="text-base font-black flex items-center gap-1.5 text-rose-700">
                  <span>💳</span>
                  <span>Customer Credit (उधारी)</span>
                </div>
                <p className="text-[11px] text-slate-500 font-semibold mt-1">
                  Customer names, mobile numbers, and old pending due amounts.
                </p>
              </button>

              <button
                type="button"
                onClick={() => {
                  setImportCategory('stockists');
                  setFile(null);
                  setParsedRows([]);
                  setStatusMsg(null);
                }}
                className={`p-3.5 rounded-2xl border text-left transition-all ${
                  importCategory === 'stockists'
                    ? 'border-teal-600 bg-teal-50/60 ring-2 ring-teal-500/20 text-slate-900 shadow-sm'
                    : 'border-slate-200 hover:bg-slate-50 text-slate-600'
                }`}
              >
                <div className="text-base font-black flex items-center gap-1.5 text-teal-700">
                  <span>🏢</span>
                  <span>Suppliers / Stockists</span>
                </div>
                <p className="text-[11px] text-slate-500 font-semibold mt-1">
                  Distributors, stockist agencies, GST numbers, and contact info.
                </p>
              </button>
            </div>
          </div>

          {/* Step 2: Download Template Or Select File */}
          <div className="bg-slate-50 rounded-2xl p-4 sm:p-5 border border-slate-200/80 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">
                  Step 2: Upload Excel / CSV File From Old Software
                </h4>
                <p className="text-xs text-slate-500 font-medium">
                  Upload the file exported from Marg, Tally, Busy, or download our ready-made template below.
                </p>
              </div>

              {/* Template Download Buttons */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleDownloadTemplate('xlsx')}
                  className="px-3 py-1.5 rounded-xl bg-white hover:bg-slate-100 text-slate-700 font-bold text-xs border border-slate-300 transition-all flex items-center gap-1 shadow-xs"
                  title="Download ready-to-fill sample Excel sheet"
                >
                  <span>📄 Sample Excel (.xlsx)</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleDownloadTemplate('csv')}
                  className="px-2.5 py-1.5 rounded-xl bg-white hover:bg-slate-100 text-slate-700 font-bold text-xs border border-slate-300 transition-all flex items-center gap-1 shadow-xs"
                  title="Download sample CSV"
                >
                  <span>CSV</span>
                </button>
              </div>
            </div>

            {/* Dropzone File Upload Input */}
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              className="hidden"
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-slate-300 hover:border-blue-500 rounded-2xl p-6 sm:p-8 text-center cursor-pointer transition-colors bg-white group select-none"
            >
              <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-2xl mx-auto group-hover:scale-110 transition-transform">
                📂
              </div>
              <h5 className="text-xs font-black text-slate-800 mt-3">
                {file ? file.name : 'Click to select or drag and drop your Excel / CSV file here'}
              </h5>
              <p className="text-[11px] text-slate-400 font-semibold mt-0.5">
                Supports Microsoft Excel (.xlsx, .xls) and Comma-Separated Values (.csv)
              </p>
              {file && (
                <span className="inline-block mt-2 px-2.5 py-0.5 rounded-full text-[10px] font-black bg-blue-100 text-blue-700 border border-blue-200">
                  {(file.size / 1024).toFixed(1)} KB • Click to choose another file
                </span>
              )}
            </div>
          </div>

          {/* Step 3: Live Preview Table */}
          {parsedRows.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between px-1">
                <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                  <span>👀</span>
                  <span>Step 3: Preview Detected Rows (First 5 of {parsedRows.length} Rows)</span>
                </h4>
                <span className="text-[11px] font-bold text-emerald-600">
                  ✓ {parsedRows.length} Total Rows Ready to Import
                </span>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto max-h-56">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-100 text-slate-700 text-[10px] uppercase font-black border-b border-slate-200">
                      <th className="p-2.5">#</th>
                      {previewColumns.slice(0, 7).map((col, idx) => (
                        <th key={idx} className="p-2.5 whitespace-nowrap">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {parsedRows.slice(0, 5).map((row, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="p-2.5 text-slate-400 font-bold">{idx + 1}</td>
                        {previewColumns.slice(0, 7).map((col, cIdx) => (
                          <td key={cIdx} className="p-2.5 font-medium text-slate-800 whitespace-nowrap">
                            {String(row[col] || '-')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>

        {/* Modal Footer Actions */}
        <div className="p-4 sm:p-5 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          <div className="text-xs font-bold text-slate-500">
            {parsedRows.length > 0
              ? `Ready to import ${parsedRows.length} records into MediFlow`
              : 'Select a file to begin importing old pharmacy data'}
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 sm:flex-none px-5 py-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold text-xs transition-colors"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleExecuteImport}
              disabled={isProcessing || parsedRows.length === 0}
              className="flex-1 sm:flex-none px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-xs transition-all shadow-md shadow-emerald-500/25 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
            >
              {isProcessing ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Importing Data into MediFlow...</span>
                </>
              ) : (
                <>
                  <span>✅ Import Data into MediFlow</span>
                </>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DataMigrationModal;
